import "@/App.css";
import "@/styles/strata-theme.css";
import { Suspense, useEffect } from "react";
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { isClockOnlyUser } from "@/lib/permissions";
import { MeetingCallProvider } from "@/contexts/MeetingCallContext";
import { CustomListsProvider } from "@/contexts/CustomListsContext";
import { RelayUnreadProvider } from "@/contexts/RelayUnreadContext";
import { AppStatusProvider } from "@/contexts/AppStatusContext";
import StatusGate from "@/components/system/StatusGate";
import { Toaster } from "@/components/ui/sonner";
import { ConfirmDialogHost } from "@/lib/confirm";

// Public pages stay eager — they're the entry points and the bundle
// is already small without the protected-route trees.
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ActivatePage from "@/pages/ActivatePage";
import BillingReactivatePage from "@/pages/BillingReactivatePage";
import BrandPreviewPage from "@/pages/BrandPreviewPage";
import PrimaryPreviewPage from "@/pages/PrimaryPreviewPage";
import PublicReportPage from "@/pages/PublicReportPage";
import ReportSignPage from "@/pages/ReportSignPage";
import DocumentSignPage from "@/pages/DocumentSignPage";
import InvoicePublicPage from "@/pages/InvoicePublicPage";
import ProposalPublicPage from "@/pages/ProposalPublicPage";
import ForcePasswordChangePage from "@/pages/ForcePasswordChangePage";
import Layout from "@/components/Layout";

// iter-156g: lazy-load every authenticated page so the initial
// bundle is small. Each page becomes its own chunk; React Router
// loads the chunk on first nav and caches it for subsequent visits.
//
// Effect on production:
//   - Initial JS bundle drops ~50-60%
//   - First nav to a page = one extra ~50-150KB chunk download
//     (parallel with the API calls that page is firing anyway)
//   - Subsequent visits = instant (chunk is cached in browser)
//
// Why these aren't lazy: LoginPage/RegisterPage/ResetPasswordPage are
// the entry points (eager keeps login feeling instant). PublicReportPage
// is hit by external recipients without auth state and needs to render
// on first paint.
const DashboardHomePage          = lazy(() => import("@/pages/DashboardHomePage"));
const GuestMeetingPage           = lazy(() => import("@/pages/GuestMeetingPage"));
const SalesPipelinePage          = lazy(() => import("@/pages/SalesPipelinePage"));
const DesignHubPage              = lazy(() => import("@/pages/DesignHubPage"));
const BudgetingPage              = lazy(() => import("@/pages/BudgetingPage"));
const BudgetWorkspacePage        = lazy(() => import("@/pages/BudgetWorkspacePage"));
const EstimatingWorkspacePage    = lazy(() => import("@/pages/EstimatingWorkspacePage"));
const EstimateBuilderPage        = lazy(() => import("@/pages/EstimateBuilderPage"));
const AgreementComposerPage      = lazy(() => import("@/pages/AgreementComposerPage"));
const LineEditorPage             = lazy(() => import("@/pages/LineEditorPage"));
const EstimateRevisionPage       = lazy(() => import("@/pages/EstimateRevisionPage"));
const AssemblyBuilderPage        = lazy(() => import("@/pages/AssemblyBuilderPage"));
const JobDetailPage              = lazy(() => import("@/pages/JobDetailPage"));
const JobReportPage              = lazy(() => import("@/pages/JobReportPage"));
const EstimatesPage              = lazy(() => import("@/pages/EstimatesPage"));
const MediaCenterPage            = lazy(() => import("@/pages/MediaCenterPage"));
const ReportsPage                = lazy(() => import("@/pages/ReportsPage"));
const WorkflowEditsReportPage    = lazy(() => import("@/pages/WorkflowEditsReportPage"));
const InternalReportPage         = lazy(() => import("@/pages/InternalReportPage"));
const ReportBuilderPage          = lazy(() => import("@/pages/ReportBuilderPage"));
const JobReportsTypePage         = lazy(() => import("@/pages/JobReportsTypePage"));
const ClientReportPage           = lazy(() => import("@/pages/ClientReportPage"));
const SettingsPage               = lazy(() => import("@/pages/SettingsPage"));
const AdminDiagnosticsPage       = lazy(() => import("@/pages/AdminDiagnosticsPage"));
const WorkflowShell              = lazy(() => import("@/pages/WorkflowShell"));
const JobsShell                  = lazy(() => import("@/pages/JobsShell"));
const PrintReportPage            = lazy(() => import("@/pages/PrintReportPage"));
const MaterialRequestsPage       = lazy(() => import("@/pages/MaterialRequestsPage"));
const JobBoardPage               = lazy(() => import("@/pages/JobBoardPage"));
// iter-156gw — DailyRoutesPage removed (replaced by Drivers List
// dispatch board on the Material Requests page).
const RelayPage                  = lazy(() => import("@/pages/RelayPage"));
const PermitsPage                = lazy(() => import("@/pages/PermitsPage"));
const LeadsPage                  = lazy(() => import("@/pages/LeadsPage"));
const PulsePage                  = lazy(() => import("@/pages/PulsePage"));
const CrewClockPage              = lazy(() => import("@/pages/CrewClockPage"));
const OnboardingWelcomePage      = lazy(() => import("@/pages/OnboardingWelcomePage"));
const PlatformAdminCompaniesPage = lazy(() => import("@/pages/PlatformAdminCompaniesPage"));
const PlatformUsagePage          = lazy(() => import("@/pages/PlatformUsagePage"));
const PlatformSecurityPage       = lazy(() => import("@/pages/PlatformSecurityPage"));
const OAuthAuthorizePage         = lazy(() => import("@/pages/OAuthAuthorizePage"));

// The AI connector Allow screen is the one other place a fresh login returns to (with its full query).
const OAUTH_AUTHORIZE_PATH = '/oauth/authorize';
const isOAuthReturn = (dest) => typeof dest === 'string' && (dest === OAUTH_AUTHORIZE_PATH || dest.startsWith(`${OAUTH_AUTHORIZE_PATH}?`));

// Fallback while a lazy chunk is loading. Matches the existing
// ProtectedRoute "Loading..." style so the visual handoff is seamless.
const PageFallback = () => (
  <div className="flex items-center justify-center min-h-[40vh]">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  if (!user) {
    // Preserve intent for the crew clock PWA so a fresh login lands there
    // (scoped whitelist — never a broad post-login redirect change).
    if (location.pathname.startsWith('/clock')) {
      try { sessionStorage.setItem('post_login_redirect', '/clock'); } catch { /* ignore */ }
    }
    if (location.pathname === OAUTH_AUTHORIZE_PATH) {
      try { sessionStorage.setItem('post_login_redirect', `${OAUTH_AUTHORIZE_PATH}${location.search}`); } catch { /* ignore */ }
    }
    return <Navigate to="/login" replace />;
  }
  // First-login: temp-password users must set their own password
  // before doing anything else in the app. The change-password page
  // itself is allowed through so users can complete the flow. Crew who came
  // in through the clock QR go back to /clock afterwards, not the full app.
  if (user.force_password_change && location.pathname !== '/force-password-change') {
    if (location.pathname.startsWith('/clock')) {
      try { sessionStorage.setItem('post_login_redirect', '/clock'); } catch { /* ignore */ }
    }
    return <Navigate to="/force-password-change" replace />;
  }
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  if (user) return <PostLoginRedirect />;
  return children;
}

// Read during render, clear in an effect: StrictMode renders twice, so clearing while rendering lost the destination.
function PostLoginRedirect() {
  let dest = null;
  try { dest = sessionStorage.getItem('post_login_redirect'); } catch { /* ignore */ }
  useEffect(() => {
    try { sessionStorage.removeItem('post_login_redirect'); } catch { /* ignore */ }
  }, []);
  return <Navigate to={dest === '/clock' || isOAuthReturn(dest) ? dest : '/'} replace />;
}

// Clock-only crew have no office surface — send them straight to the mobile
// /clock screen whenever they'd otherwise land on the full app shell. Guards
// ONLY the app-shell ("/") subtree, so /clock itself never loops.
function ClockOnlyGuard({ children }) {
  const { user } = useAuth();
  if (isClockOnlyUser(user)) return <Navigate to="/clock" replace />;
  return children;
}

// Redirect legacy deep-links /jobs/:jobId/workflow → /workflow?job_id=:jobId
// (the canonical Workflow page reads `job_id` from the query string).
function WorkflowRedirect() {
  const { jobId } = useParams();
  return <Navigate to={`/workflow?job_id=${encodeURIComponent(jobId || '')}`} replace />;
}

function App() {
  return (
    <AppStatusProvider>
      <AuthProvider>
        <BrowserRouter>
          <StatusGate />
          <Suspense fallback={<PageFallback />}>
            <Routes>
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
            <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
            {/* Force-password-change is a protected route (user is
                authenticated with the temp password) but lives outside
                the main app shell — no Layout, no sidebar. The
                ProtectedRoute guard auto-redirects users with the
                force_password_change flag to here. */}
            <Route path="/force-password-change" element={<ProtectedRoute><ForcePasswordChangePage /></ProtectedRoute>} />
            <Route path="/activate" element={<ActivatePage />} />
            <Route path="/billing/reactivate" element={<BillingReactivatePage />} />
            <Route path="/brand-preview" element={<BrandPreviewPage />} />
            <Route path="/primary-preview" element={<PrimaryPreviewPage />} />
            <Route path="/report/:shareToken" element={<PublicReportPage />} />
            <Route path="/meet/guest/:token" element={<GuestMeetingPage />} />
            {/* Web Driver Portal retired — the native mobile app replaces it.
                Redirect any bookmarked portal links to the normal login. */}
            <Route path="/driver" element={<Navigate to="/login" replace />} />
            <Route path="/driver/login" element={<Navigate to="/login" replace />} />
            <Route path="/print-report/:jobId" element={<ProtectedRoute><PrintReportPage /></ProtectedRoute>} />
            {/* Field crew Time Clock — standalone (no app shell), mobile-first,
                installable as its own Android PWA. Any authed user reaches it;
                the page + backend gate on the time_clock.clock permission. */}
            <Route path="/clock" element={<ProtectedRoute><CrewClockPage /></ProtectedRoute>} />
            <Route path={OAUTH_AUTHORIZE_PATH} element={<ProtectedRoute><OAuthAuthorizePage /></ProtectedRoute>} />
            {/* iter-156ih — Public client report: renders the SAME print
                page as the PDF (public mode, token-gated, no auth). */}
            <Route path="/r/:shareToken" element={<PrintReportPage />} />
            {/* Loop W2 — public per-signer envelope link (token-gated, no auth). */}
            <Route path="/sign-report/:token" element={<ReportSignPage />} />
            {/* Loop D8 — public document signing link (token-gated, no auth). */}
            <Route path="/sign/:token" element={<DocumentSignPage />} />
            {/* Loop 15 — a client's private invoice link (token-gated, no auth). */}
            <Route path="/invoice/:token" element={<InvoicePublicPage />} />
            {/* Agreements part 1 — a client's private proposal link (token-gated, no auth) + the estimator's preview of it. */}
            <Route path="/proposal/:token" element={<ProposalPublicPage />} />
            <Route path="/" element={<ProtectedRoute><ClockOnlyGuard><CustomListsProvider><RelayUnreadProvider><MeetingCallProvider><Layout /></MeetingCallProvider></RelayUnreadProvider></CustomListsProvider></ClockOnlyGuard></ProtectedRoute>}>
              <Route index element={<DashboardHomePage />} />
              <Route path="onboarding/welcome" element={<OnboardingWelcomePage />} />
              <Route path="admin/companies" element={<PlatformAdminCompaniesPage />} />
              <Route path="admin/usage" element={<PlatformUsagePage />} />
              <Route path="admin/security" element={<PlatformSecurityPage />} />
              <Route path="d/view/:token" element={<DashboardHomePage />} />
              <Route path="jobs" element={<JobsShell />} />
              <Route path="jobs/:jobId" element={<JobDetailPage />} />
              <Route path="jobs/:jobId/reports/:reportId" element={<JobReportPage />} />
              <Route path="jobs/:jobId/workflow" element={<WorkflowRedirect />} />
              <Route path="kits" element={<EstimatesPage />} />
              <Route path="workflow" element={<WorkflowShell />} />
              <Route path="media" element={<MediaCenterPage />} />
              <Route path="media-v2" element={<MediaCenterPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="reports/workflow-edits" element={<WorkflowEditsReportPage />} />
              <Route path="reports/internal/new" element={<ReportBuilderPage />} />
              <Route path="reports/internal/:definitionId/edit" element={<ReportBuilderPage />} />
              <Route path="reports/internal/:definitionId" element={<InternalReportPage />} />
              <Route path="reports/job-reports/:reportType" element={<JobReportsTypePage />} />
              <Route path="reports/client/:jobId" element={<ClientReportPage />} />
              <Route path="reports/client/:jobId/:reportId" element={<ClientReportPage />} />
              <Route path="material-requests" element={<MaterialRequestsPage />} />
              {/* iter-156gw — daily-routes deleted; redirect any
                  bookmarked links to the Drivers List dispatch
                  board on the Material Requests page. */}
              <Route path="daily-routes" element={<Navigate to="/material-requests" replace />} />
              {/* Aspire Import folded into the Jobs page as a tab.
                  Redirect the old standalone route + bookmarks. */}
              <Route path="import" element={<Navigate to="/jobs?tab=aspire-import" replace />} />
              <Route path="relay" element={<RelayPage />} />
              <Route path="permits" element={<PermitsPage />} />
              <Route path="morning-meeting" element={<Navigate to="/relay?tab=morning-meeting" replace />} />
              <Route path="sales-pipeline" element={<SalesPipelinePage />} />
              <Route path="design-hub" element={<DesignHubPage />} />
              <Route path="budgeting" element={<BudgetingPage />} />
              <Route path="budgeting/:budgetId" element={<BudgetWorkspacePage />} />
              <Route path="estimating" element={<Navigate to="/estimating/estimates" replace />} />
              <Route path="estimating/estimates" element={<EstimatingWorkspacePage tab="estimates" />} />
              <Route path="estimating/estimates/:estimateId" element={<EstimateBuilderPage />} />
              <Route path="estimating/estimates/:estimateId/agreement" element={<AgreementComposerPage />} />
              <Route path="estimating/estimates/:estimateId/lines/:lineId" element={<LineEditorPage />} />
              <Route path="estimating/estimates/:estimateId/revisions/:number" element={<EstimateRevisionPage />} />
              <Route path="estimating/assemblies" element={<EstimatingWorkspacePage tab="assemblies" />} />
              <Route path="estimating/templates" element={<EstimatingWorkspacePage tab="takeoffs" />} />
              <Route path="estimating/invoices" element={<EstimatingWorkspacePage tab="invoices" />} />
              <Route path="estimating/takeoffs" element={<Navigate to="/estimating/templates" replace />} />
              <Route path="estimating/photos" element={<Navigate to="/estimating/estimates" replace />} />
              <Route path="estimating/assemblies/:assemblyId" element={<AssemblyBuilderPage />} />
              <Route path="job-board" element={<JobBoardPage />} />
              <Route path="leads" element={<LeadsPage />} />
              <Route path="pulse" element={<PulsePage />} />
              {/* Workflow Templates folded into the Workflow page as a
                  tab. Redirect the old standalone route + bookmarks
                  (incl. the legacy /templates → /workflow-templates). */}
              <Route path="workflow-templates" element={<Navigate to="/workflow?tab=templates" replace />} />
              <Route path="templates" element={<Navigate to="/workflow?tab=templates" replace />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="admin/diagnostics" element={<AdminDiagnosticsPage />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      <ConfirmDialogHost />
      </AuthProvider>
    </AppStatusProvider>
  );
}

export default App;
